// Reference: https://usehooks.com/useScript/
import { useEffect, useState } from 'react';

const loadedScripts: string[] = [];
const loadingScripts: { [key: string]: HTMLScriptElement } = {};

export enum ScriptStatus {
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

type Props = {
  src: string;
  pause?: boolean;
};

export function useScript({ src, pause }: Props): [ScriptStatus] {
  const [status, setStatus] = useState<ScriptStatus>(ScriptStatus.Loading);

  useEffect(() => {
    if (pause) {
      return;
    }
    if (loadedScripts.includes(src)) {
      setStatus(ScriptStatus.Success);
    } else {
      let script: HTMLScriptElement;

      if (loadingScripts[src]) {
        script = loadingScripts[src];
      } else {
        script = document.createElement('script');
        script.src = src;
        script.async = true;
        document.body.appendChild(script);
        loadingScripts[src] = script;
      }

      const onLoad = () => {
        delete loadingScripts[src];
        loadedScripts.push(src);
        setStatus(ScriptStatus.Success);
      };

      const onError = () => {
        delete loadingScripts[src];
        script.remove();
        setStatus(ScriptStatus.Error);
      };

      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);

      return () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };
    }
  }, [pause, src]);

  return [status];
}

export const loadScript = (src: string) => {
  return new Promise<void>((resolve, reject) => {
    if (loadedScripts.includes(src)) {
      resolve();
    } else {
      let script: HTMLScriptElement;

      script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);

      const onLoad = () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
        loadedScripts.push(src);
        resolve();
      };

      const onError = () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
        reject();
      };

      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);
    }
  });
};
